import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Plus, Minus, Star } from 'lucide-react';
import ServiceCard from './ServiceCard';
import { companyInfo, services, testimonials, faqs } from '../mock';

const HomePage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Contact form. Posts to /api/contact, which emails via Resend — no database.
  const initialForm = {
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    // Honeypot: hidden from people, irresistible to bots.
    website: ''
  };
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setStatus('error');
        setErrorMessage(data.error || "We couldn't send your message just now.");
        return;
      }

      setStatus('sent');
      setForm(initialForm);
    } catch {
      setStatus('error');
      setErrorMessage("We couldn't reach our server. Check your connection and try again.");
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Logo: scroll to top when already home, otherwise go home.
  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      e.preventDefault();
      navigate('/');
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="homepage">
      {/* Header */}
      <header className="design-header">
        <div className="nav-container">
          <div className="logo-section">
            <a
              href="/"
              className="company-logo-container"
              onClick={handleLogoClick}
              aria-label={`${companyInfo.name} — back to top`}
            >
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt=""
                className="company-logo-mark"
                width="48"
                height="48"
              />
              <div className="logo-text">
                <h2 className="company-name">{companyInfo.name}</h2>
                <p className="company-tagline-header">{companyInfo.tagline}</p>
              </div>
            </a>
          </div>
          <nav className="nav-menu">
            <a href="#services" className="nav-link">Services</a>
            <a href="#testimonials" className="nav-link">Testimonials</a>
            <a href="#contact" className="nav-link">Contact</a>
            <a href={`tel:${companyInfo.phone}`} className="btn-accent">Call Now</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-frame">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="hero-copy-inner">
            <h1 className="display-lg text-primary">{companyInfo.name}</h1>
            <p className="body-xl text-secondary space-16">{companyInfo.tagline}</p>
            <p className="body-lg text-muted space-32">{companyInfo.description}</p>
            <div className="hero-actions">
              <a href="#contact" className="btn-primary">Get a Free Quote</a>
            </div>
          </div>
        </motion.div>

        <div className="hero-media">
          <img
            src={`${process.env.PUBLIC_URL}/hero/stjohns-skyline.jpg`}
            alt="Downtown St. John's, Newfoundland, seen from Signal Hill"
            width="1600"
            height="1150"
          />
        </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="heading-1 text-primary">Our Companies</h2>
            <p className="body-lg text-secondary">
              Specialized subsidiaries providing expert services in their respective fields
            </p>
          </div>
          
          <div className="design-grid">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="heading-1 text-primary">What Our Clients Say</h2>
            <p className="body-lg text-secondary">
              Trusted by families and businesses across the region
            </p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="testimonial-card"
              >
                <div className="stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="star-filled" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div>
                    <p className="author-name">{testimonial.name}</p>
                    <p className="author-company">{testimonial.company}</p>
                    <p className="service-used">Service: {testimonial.service}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <div className="section-header">
            <h2 className="heading-1 text-primary">Frequently Asked Questions</h2>
          </div>
          
          {faqs.map((faq, index) => (
            <div key={faq.id} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFaq(index)}
              >
                <span>{faq.question}</span>
                <div className={`faq-icon ${openFaq === index ? 'open' : ''}`}>
                  {openFaq === index ? <Minus size={24} /> : <Plus size={24} />}
                </div>
              </button>
              {openFaq === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="faq-answer"
                >
                  <p>{faq.answer}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="heading-1 text-primary">Get In Touch</h2>
            <p className="body-lg text-secondary">
              Contact our main office for coordinated services or general inquiries
            </p>
          </div>
          
          <div className="contact-layout">
          <form className="contact-form" onSubmit={handleContactSubmit} noValidate>
            <h3 className="contact-form-title">Send us a message</h3>
            <p className="contact-form-sub">
              Tell us what you need and we'll point you to the right company.
            </p>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="cf-name">Name <span aria-hidden="true">*</span></label>
                <input
                  id="cf-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={form.name}
                  onChange={handleField}
                />
              </div>
              <div className="form-field">
                <label htmlFor="cf-email">Email <span aria-hidden="true">*</span></label>
                <input
                  id="cf-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={handleField}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="cf-phone">Phone</label>
                <input
                  id="cf-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleField}
                />
              </div>
              <div className="form-field">
                <label htmlFor="cf-service">Enquiring about</label>
                <select
                  id="cf-service"
                  name="service"
                  value={form.service}
                  onChange={handleField}
                >
                  <option value="">Select a company</option>
                  <option value="it">Reliable IT Solutions</option>
                  <option value="home">Reliable Home Care</option>
                  <option value="auto">Reliable Auto Care</option>
                  <option value="multiple">More than one company</option>
                  <option value="other">General enquiry</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="cf-message">Message <span aria-hidden="true">*</span></label>
              <textarea
                id="cf-message"
                name="message"
                rows={5}
                required
                value={form.message}
                onChange={handleField}
              />
            </div>

            {/* Honeypot — hidden from people, left in the DOM for bots */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="cf-website">Leave this field empty</label>
              <input
                id="cf-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={handleField}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send Message'}
            </button>

            <p className="form-status" role="status" aria-live="polite">
              {status === 'sent' && (
                <span className="form-ok">Thanks — we'll reply within 24 hours.</span>
              )}
              {status === 'error' && (
                <span className="form-err">{errorMessage}</span>
              )}
            </p>
          </form>

          <div className="contact-info">
            <div className="contact-item">
              <div className="contact-icon-wrap">
                <Phone className="contact-icon" size={22} />
              </div>
              <div>
                <p className="contact-label">Phone</p>
                <p className="contact-note">Call us directly</p>
                <a href={`tel:${companyInfo.phone}`} className="contact-value">{companyInfo.phone}</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon-wrap">
                <Mail className="contact-icon" size={22} />
              </div>
              <div>
                <p className="contact-label">Email</p>
                <p className="contact-note">Response within 24 hours</p>
                <a href={`mailto:${companyInfo.email}`} className="contact-value">{companyInfo.email}</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon-wrap">
                <MapPin className="contact-icon" size={22} />
              </div>
              <div>
                <p className="contact-label">Address</p>
                <p className="contact-note">Main office</p>
                <p className="contact-value">{companyInfo.address}</p>
              </div>
            </div>

            <div className="contact-map">
              <iframe
                title="Map of downtown St. John's, NL"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-52.7280%2C47.5570%2C-52.6930%2C47.5740&layer=mapnik&marker=47.5657%2C-52.7093"
                loading="lazy"
              />
              <a
                className="map-directions"
                href="https://www.google.com/maps/search/?api=1&query=Downtown+St.+John%27s%2C+NL%2C+Canada"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get directions
              </a>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="design-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="footer-title">{companyInfo.name}</h3>
              <p className="footer-text">Your trusted partner for comprehensive solutions across multiple service areas.</p>
            </div>
            <div className="footer-section">
              <h4 className="footer-subtitle">Our Companies</h4>
              <ul className="footer-links">
                {services.map(service => (
                  <li key={service.id}>
                    <a
                      href={service.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-link"
                    >
                      {service.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-subtitle">Contact</h4>
              <p className="footer-text">{companyInfo.phone}</p>
              <p className="footer-text">{companyInfo.email}</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} {companyInfo.name} All rights reserved.
            </p>
            <p className="footer-credit">
              Header photo:{' '}
              <a
                href="https://commons.wikimedia.org/wiki/File:St._John%27s_skyline_June_2025.jpg"
                target="_blank"
                rel="noopener noreferrer"
              >
                “St. John's skyline June 2025”
              </a>{' '}
              by Qwexcxewq,{' '}
              <a
                href="https://creativecommons.org/licenses/by/4.0/"
                target="_blank"
                rel="noopener noreferrer"
              >
                CC BY 4.0
              </a>.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;